import { Injectable, inject } from '@angular/core';
import type { ShoppingCartSubject } from '@app/interface/subject/shopping-cart-subject.interface';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { EnhancementStore } from '@app/store/data/enhancement/enhancement.store';
import { DEFAULT_PERSONALIZATION_MAX_LENGTH } from './student-personalization.constants';
import { getPersonalizationNameError } from './student-personalization.validation';

type PersonalizationSubject = Pick<
  ShoppingCartSubject,
  'id' | 'subjectFirstName' | 'portraitDashboard' | 'allowPersonalizationNamePrefill'
>;

@Injectable({
  providedIn: 'root'
})
export class StudentPersonalizationService {
  private readonly enhancementStore = inject(EnhancementStore);
  private readonly jobStore = inject(angularjs.JobStore);
  private readonly orderFormService = inject(angularjs.OrderFormService);
  private readonly subjectStore = inject(angularjs.SubjectStore);

  hasPersonalization(subject: Pick<ShoppingCartSubject, 'id'>): boolean {
    return this.enhancementStore.hasPersonalization()(subject.id);
  }

  hasSelectedPersonalization(subject: Pick<ShoppingCartSubject, 'id'>): boolean {
    return Boolean(this.enhancementStore.getSelectedPersonalization()(subject.id));
  }

  getMaxLength(subject?: Pick<ShoppingCartSubject, 'id'> | null): number {
    if (!subject) {
      return DEFAULT_PERSONALIZATION_MAX_LENGTH;
    }

    return this.enhancementStore.getPersonalizationMaxLength()(subject.id);
  }

  getError(value: string, maxLength: number): string | null {
    return getPersonalizationNameError(value, maxLength);
  }

  getExistingName(subject?: PersonalizationSubject | null): string {
    if (!subject) {
      return '';
    }

    const enhancementName = this.enhancementStore.getPersonalizationName()(subject.id);
    if (enhancementName) {
      return enhancementName;
    }

    if (this.shouldUseSubjectFirstName(subject)) {
      return subject.subjectFirstName?.trim() ?? '';
    }

    return '';
  }

  private shouldUseSubjectFirstName(subject: PersonalizationSubject): boolean {
    if (!subject.subjectFirstName?.trim()) {
      return false;
    }

    if (this.subjectStore.isGeneratedFirstNamePlaceholder?.(subject.subjectFirstName)) {
      return false;
    }

    if (subject.allowPersonalizationNamePrefill) {
      return true;
    }

    return this.isPortraitOrPictureDayFlow() || this.isReorderFlow(subject.portraitDashboard?.jobKey);
  }

  private isPortraitOrPictureDayFlow(): boolean {
    const currentFlow = this.orderFormService.getCurrentFlow?.();

    return Array.isArray(currentFlow)
      ? currentFlow.includes('portraitIdForm') || currentFlow.includes('pictureDayIdForm')
      : false;
  }

  private isReorderFlow(jobKey?: string | null): boolean {
    return Boolean(this.jobStore.isReorder?.(jobKey ?? undefined) || this.jobStore.isProofOrder?.(jobKey ?? undefined));
  }
}
